import { Injectable, Renderer2, RendererFactory2, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslationService } from './translation.service';
import { environment } from '../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class AppRedirectService {
    private readonly showSpinnerState = signal<boolean>(false);
    readonly showSpinner$ = toObservable(this.showSpinnerState);
    appStoreLink: string = '';
    playStoreLink: string = '';
    deepLinkUrl: string = '';
    private readonly renderer: Renderer2;
    appStoreRedirectionLink: any;

    constructor(
        private readonly translationService: TranslationService,
        readonly rendererFactory: RendererFactory2,
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    public getSmartBannerLinks() {
        this.playStoreLink = this.translationService.cpConfig.playStoreLink;
        this.appStoreLink = this.translationService.cpConfig.appStoreLink;
        this.deepLinkUrl = this.translationService.cpConfig.deepLinkUrl;
        this.appStoreRedirectionLink = this.translationService.cpConfig.appStoreLink;
    }

    public detectAppleDevice(): boolean {
        const userAgent = navigator.userAgent || navigator.vendor;
        return /Mac|iPhone|iPad/.test(userAgent);
    }

    public isAndroidDevice(): boolean {
        return /android/i.test(navigator.userAgent || '');
    }


    /**
     * The shared page URL is itself a universal link for the app, but iOS only
     * honours universal links navigated to from a *different* domain — so the
     * "open in app" flow bounces through the associated stories4.me redirect
     * page. The /app-redirect route keeps the store fallback when the app is
     * not installed. Pages outside the universal-link paths fall back to the
     * custom scheme.
     *
     * Note: the open-app script parses `?path=` raw (location.replace of the
     * literal value), so it must NOT be URI-encoded.
     */
    public buildOpenAppUrl(): string {
        const pathWithoutLang = window.location.pathname.replace(/^\/[^/]+/, '');
        if (!/\/stories\/|\/verify\//.test(pathWithoutLang)) {
            return this.deepLinkUrl;
        }
        const appRedirectUrl = `${environment.domain}/app-redirect${pathWithoutLang}${window.location.search}`;
        return `${environment.appRedirectUrl}/open-app?path=${appRedirectUrl}`;
    }

    /**
     * Builds an Android intent:// URI for the given deep link. Chrome resolves the
     * scheme natively: it opens the app when installed, and follows
     * browser_fallback_url (Play Store) otherwise — avoiding the
     * net::ERR_UNKNOWN_URL_SCHEME "this page does not lead anywhere" error page
     * that a bare window.location navigation to a custom scheme produces.
     */
    public buildAndroidIntentUrl(deepLinkUrl: string, fallbackUrl: string): string {
        const match = /^([a-zA-Z][a-zA-Z0-9+.-]*):\/\/(.+)$/.exec(deepLinkUrl);
        if (!match) {
            return deepLinkUrl;
        }
        const scheme = match[1];
        const path = match[2];
        const packageId = /[?&]id=([a-zA-Z0-9._]+)/.exec(fallbackUrl)?.[1];
        let intentUrl = `intent://${path}#Intent;scheme=${scheme};`;
        if (packageId) {
            intentUrl += `package=${packageId};`;
        }
        if (fallbackUrl) {
            intentUrl += `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};`;
        }
        return `${intentUrl}end`;
    }

    /**
     * The URL a real "Open in app" anchor should point to — the universal-link
     * bounce on iOS, an intent:// URI on Android.
     */
    public getOpenAppUrl(): string {
        this.getSmartBannerLinks();
        const redirectUrl = this.detectAppleDevice() ? this.playStoreLink : this.appStoreLink;
        return this.isAndroidDevice()
            ? this.buildAndroidIntentUrl(this.deepLinkUrl, redirectUrl)
            : this.buildOpenAppUrl();
    }

    /**
     * Starts app-opened detection and the store fallback. Call this when the
     * deep link is triggered by a real anchor tap — the browser performs the
     * navigation itself, so only the fallback needs JS.
     */
    public watchOpenAppAttempt(): void {
        this.showSpinnerState.set(true);

        const redirectUrl = this.detectAppleDevice() ? this.playStoreLink : this.appStoreLink;

        let appOpened = false;

        const handleAppOpened = () => {
            appOpened = true;
            this.showSpinnerState.set(false);
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible') {
                handleAppOpened();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handleAppOpened);
        window.addEventListener('blur', handleAppOpened);

        setTimeout(() => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', handleAppOpened);
            window.removeEventListener('blur', handleAppOpened);
            if (!appOpened && redirectUrl) {
                this.openSchemeViaAnchor(redirectUrl);
                this.showSpinnerState.set(false);
            }
        }, 3000);
    }

    public triggerGoToApp(): void {
        const appUrl = this.getOpenAppUrl();

        // Register listeners before attempting the deep link so a fast app switch is not missed
        this.watchOpenAppAttempt();

        // Trigger via a real anchor click — a link-activated navigation, which
        // is what iOS requires for the universal link to open the app.
        this.openSchemeViaAnchor(appUrl);
    }

    private openSchemeViaAnchor(url: string): void {
        const anchor = this.renderer.createElement('a');
        anchor.href = url;
        anchor.style.display = 'none';
        this.renderer.appendChild(document.body, anchor);
        anchor.click();
        this.renderer.removeChild(document.body, anchor);
    }

    public redirectUserToStoreBasedOnDeviceRegion(): void {
        const storeUrl = this.detectAppleDevice() ? this.playStoreLink : this.appStoreLink;
        setTimeout(() => {
            // Page still visible = app NOT installed → store
            if (document.visibilityState === 'visible') {
                if (storeUrl) {
                    window.location.href = storeUrl;
                }
                this.showSpinnerState.set(false);
            }
        }, 3000);
    }

    public openDeepLinkUrls(path: string) {
        const appRedirectUrl = environment.appRedirectUrl;
        const redirectionUrl = `${appRedirectUrl}/open-app?path=${path}`;

        const isIos = this.detectAppleDevice();

        if (isIos) {
            window.location.href = redirectionUrl;
        } else {
            // Desktop/Android usually handle window.open better
            window.open(redirectionUrl, '_blank');
        }
    }

    public closeSpinner(): void {
        this.showSpinnerState.set(false);
    }

    checkBannerPresence(): boolean {
        return !!this.renderer.selectRootElement('.banner-area', true);
    }

    private updateSpinnerState(isVisible: boolean): void {
        this.showSpinnerState.set(isVisible);
    }
}
